import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useCallback, useState } from "react";
import Sentry from "@similarweb/sentry-logger";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Button, IconButton, Toast } from "@similarweb/ui-components";
import styled from "styled-components";

import { AGENT_STEPS } from "../../CustomHooks/customIndustryAgent.types";
import { customIndustryAgentRunService } from "../../services/customIndustryAgentRun/customIndustryAgentRunService";
import { navigateToMarketBuilder } from "../../services/navigateToMarketBuilder";
import { openCreatedIndustry } from "../../services/openCreatedIndustry";
import { CancelRunModal } from "../aiBuilderV2/CancelRunModal";
import { getRejectionToastMessage } from "../aiBuilderV2/rejectionCopy";
import { getStepLabel } from "../aiBuilderV2/stepLabels";
import { currentStepIndex, currentStepNumber, hasPublishedIndustry, runStepCount } from "../aiBuilderV2/stepPlan";
import { translateWithFallback as t } from "../aiBuilderV2/translateWithFallback";
import { useCustomIndustryAgentBannerVisible, useCustomIndustryAgentRunSnapshot } from "./useCustomIndustryAgentBanner";

const { carbon } = colorsPalettes;

const TOAST_Z_INDEX = 10050;

/* Figma nodes 167:8466 (running) and 167:9830 (done) — the app's own `Toast` (dark carbon/500
   pill) replaces the old draggable white banner. It's pinned bottom-center instead of a movable
   floating window, and the whole toast is one click target that opens the maximized builder or
   created industry — a toast's usual "tap to open" affordance. */
const ToastPosition = styled.div`
    position: fixed;
    left: 50%;
    bottom: 16px;
    transform: translateX(-50%);
    z-index: ${TOAST_Z_INDEX};
    max-width: calc(100vw - 48px);
    cursor: pointer;
`;

const ToastLabel = styled.span`
    ${setFontVariant("button", carbon[0])}
    white-space: nowrap;
`;

const ToastMessage = styled.span`
    ${setFontVariant("input2", carbon[0])}
    white-space: nowrap;
`;

// Counted over the steps this run actually has: a run with no segment budget skips two of
// them, so "4/6" would stall on a number it can never reach.
const getProgressLabel = (completedSteps: number, maxSegments: number): string => {
    const totalSteps = runStepCount(maxSegments);
    const stepNumber = Math.min(currentStepNumber(completedSteps, maxSegments), totalSteps);
    const stepKey = AGENT_STEPS[currentStepIndex(completedSteps, maxSegments)];

    return `${getStepLabel(stepKey)} ${stepNumber}/${totalSteps}`;
};

// A click on one of the toast's own action buttons (Cancel, dismiss) must not also trigger the
// whole-toast click-to-maximize it sits inside. `Toast`'s own `onClose` hands back no event to
// stop that bubbling, so every action button here is passed through `secondaryItems` instead of
// that prop — plain buttons this component fully controls, each stopping its own click.
const stopPropagationThen = (handler: () => void) => (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    handler();
};

type ToastShellProps = {
    onNavigate: () => void;
    secondaryItems: ReactNode[];
    children: ReactNode;
};

const ToastShell = ({ onNavigate, secondaryItems, children }: ToastShellProps) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        // A key on one of the toast's own buttons bubbles here; it is that button's action only.
        if (event.target !== event.currentTarget) {
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onNavigate();
        }
    };

    return (
        <ToastPosition
            role="button"
            tabIndex={0}
            data-automation="custom-industry-agent-toast"
            onClick={onNavigate}
            onKeyDown={handleKeyDown}
        >
            <Toast secondaryItems={secondaryItems}>{children}</Toast>
        </ToastPosition>
    );
};

export const CustomIndustryAgentBanner = () => {
    const snapshot = useCustomIndustryAgentRunSnapshot();
    const isVisible = useCustomIndustryAgentBannerVisible(snapshot);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    // Every way into the builder from the run's own toast may show its error, including a run
    // that fails while the route changes.
    const handleNavigateToBuilder = useCallback(() => {
        navigateToMarketBuilder(snapshot.countryId, { showRunError: true });
    }, [snapshot.countryId]);

    const handleOpenIndustry = useCallback(() => {
        if (!snapshot.industry) {
            return;
        }

        // Either way the run lands on the hand-off error, whose toast offers to open it again.
        void openCreatedIndustry(snapshot.industry, snapshot.countryId ?? undefined)
            .then((opened) => {
                if (!opened) {
                    customIndustryAgentRunService.reportHandoffFailure();
                }
            })
            .catch((error) => {
                Sentry.error("CustomIndustryAgent failed to open the created industry", error);
                customIndustryAgentRunService.reportHandoffFailure();
            });
    }, [snapshot.countryId, snapshot.industry]);

    const handleDismiss = useCallback(() => {
        customIndustryAgentRunService.dismiss();
    }, []);

    // Cancelling from the toast destroys the same run as cancelling from the builder's own
    // loader, so it asks with the same modal instead of acting on a single stray click.
    const handleCancelRequest = useCallback(() => {
        setIsCancelModalOpen(true);
    }, []);

    const handleCancelConfirm = useCallback(() => {
        setIsCancelModalOpen(false);
        customIndustryAgentRunService.cancel();
    }, []);

    if (!isVisible) {
        return null;
    }

    const dismissButton = (
        <IconButton
            key="dismiss"
            type="flat"
            iconName="clear"
            iconSize="xs"
            data-automation="custom-industry-agent-toast-dismiss"
            onClick={stopPropagationThen(handleDismiss)}
        />
    );

    if (snapshot.status === "done" && snapshot.industry) {
        return (
            <ToastShell
                onNavigate={handleOpenIndustry}
                secondaryItems={[dismissButton]}
            >
                <ToastMessage>{t("market.builder.agentBanner.done.title", "Your industry is ready")}</ToastMessage>
                <ToastLabel data-automation="custom-industry-agent-toast-industry-name">
                    {snapshot.industry.industryName}
                </ToastLabel>
            </ToastShell>
        );
    }

    // The industry exists, only opening it failed: the way on is opening it, not building it again.
    if (snapshot.status === "error" && snapshot.industry) {
        return (
            <ToastShell
                onNavigate={handleOpenIndustry}
                secondaryItems={[
                    <Button
                        key="open-industry"
                        type="flat"
                        data-automation="custom-industry-agent-toast-open-industry"
                        onClick={stopPropagationThen(handleOpenIndustry)}
                    >
                        {t("market.builder.agentBanner.error.openIndustry", "Open industry")}
                    </Button>,
                    dismissButton,
                ]}
            >
                <ToastLabel data-automation="custom-industry-agent-toast-title">
                    {t("market.builder.agentBanner.rejected.title", "Custom industry builder")}
                </ToastLabel>
                <ToastMessage data-automation="custom-industry-agent-toast-copy">
                    {t(
                        "market.builder.agentBanner.error.handoffFailed",
                        "Your industry is ready, but opening it failed",
                    )}
                </ToastMessage>
            </ToastShell>
        );
    }

    // Figma node 224:7817 — the link and the toast itself both lead back to the builder, which
    // shows the prompt ready to be rephrased (rejected) or submitted again (failed).
    if (snapshot.status === "rejected" || snapshot.status === "error") {
        const isRejected = snapshot.status === "rejected";

        return (
            <ToastShell
                onNavigate={handleNavigateToBuilder}
                secondaryItems={[
                    <Button
                        key="back-to-builder"
                        type="flat"
                        data-automation={
                            isRejected ? "custom-industry-agent-toast-rephrase" : "custom-industry-agent-toast-retry"
                        }
                        onClick={stopPropagationThen(handleNavigateToBuilder)}
                    >
                        {isRejected
                            ? t("market.builder.agentBanner.rejected.rephrase", "Try rephrasing")
                            : t("market.builder.agentBanner.error.retry", "Try again")}
                    </Button>,
                    dismissButton,
                ]}
            >
                <ToastLabel data-automation="custom-industry-agent-toast-title">
                    {t("market.builder.agentBanner.rejected.title", "Custom industry builder")}
                </ToastLabel>
                <ToastMessage data-automation="custom-industry-agent-toast-copy">
                    {isRejected
                        ? getRejectionToastMessage(snapshot.errorCode)
                        : t("market.builder.agentBanner.error.message", "Something went wrong")}
                </ToastMessage>
            </ToastShell>
        );
    }

    return (
        <>
            <ToastShell
                onNavigate={handleNavigateToBuilder}
                secondaryItems={
                    hasPublishedIndustry(snapshot.completedSteps)
                        ? []
                        : [
                              <Button
                                  key="cancel"
                                  type="flatWarning"
                                  data-automation="custom-industry-agent-toast-cancel"
                                  onClick={stopPropagationThen(handleCancelRequest)}
                              >
                                  {t("common.cancel", "Cancel")}
                              </Button>,
                          ]
                }
            >
                <ToastLabel data-automation="custom-industry-agent-toast-title">
                    {t("market.builder.agentBanner.generating.title", "Building your industry")}
                </ToastLabel>
                <ToastMessage data-automation="custom-industry-agent-toast-step">
                    {getProgressLabel(snapshot.completedSteps, snapshot.maxSegments)}
                </ToastMessage>
            </ToastShell>
            <CancelRunModal
                isOpen={isCancelModalOpen}
                onStay={() => setIsCancelModalOpen(false)}
                onCancelRun={handleCancelConfirm}
            />
        </>
    );
};
